import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { BaseRenderComponent } from 'cms/workspace/workspace-generic-classes/base-render.component';
import { DurationUnits } from 'luxon';
import { getStringParam } from 'render/render-shared/ui/render-product-prices/render-product-prices-utils';
import { GrowthbookConfig, RenderGrowthbookConfigsService } from 'render/services/render-growthbook-configs/render-growthbook-configs.service';
import { RenderSlugService } from 'render/services/render-slug.service';
import { RenderStateService } from 'render/services/render-state/render-state.service';
import {
  Observable, combineLatest, first, from, interval, of, switchMap
} from 'rxjs';
import { InditexWindow, PromotionEvent } from 'shared/models/inditex-window.models';

import { PieceFinalCountdownSettings } from 'extensions/core/piece-final-countdown/front/models/piece-final-countdown.models';
import { DurationWithPercent, FinalCountDownService } from 'extensions/core/piece-final-countdown/front/services/final-countdown.service';


@Component({
  selector: 'piece-final-countdown-render',
  templateUrl: './piece-final-countdown-render.component.html',
  styleUrls: ['./piece-final-countdown-render.component.scss']
})
export class PieceFinalCountdownRenderComponent
  extends BaseRenderComponent<PieceFinalCountdownSettings>
  implements OnInit {

  public timeToCountdown$!: Observable<DurationWithPercent | undefined>;

  constructor(
    @Inject('Window') private readonly window: InditexWindow,
    private readonly finalCountdownService: FinalCountDownService,
    private readonly renderSlugService: RenderSlugService,
    private readonly renderStateService: RenderStateService,
    private readonly renderGrowthbookService: RenderGrowthbookConfigsService
  ) {
    super();
  }

  @HostBinding('class')
  public get classes(): string {
    return this.settings.styles.customClassesList ?? '';
  }

  @HostBinding('class.theme-box')
  public get themeBox(): boolean {
    return this.settings.styles.theme === 'box';
  }

  public get dateStyle(): PieceFinalCountdownSettings['general']['dateStyle'] | undefined {
    return this.settings.general.dateStyle;
  }

  public get labelStyle(): PieceFinalCountdownSettings['general']['labelStyle'] | undefined {
    return this.settings.general.labelStyle;
  }

  public ngOnInit(): void {
    const generalSettings = this.settings.general;
    const units = this.getUnits(generalSettings.dateStyle);

    const currentDate$ = this.renderStateService.getTimeTravelTo$(this.renderSlugService.getSlug()).pipe(
      switchMap((timeTravelTo) => this.finalCountdownService.getCurrentDate$(timeTravelTo ?? undefined).pipe()),
      first()
    );

    let nearestPromotionEvent$: Observable<PromotionEvent | undefined> = of(undefined);

    if (generalSettings.helperNextEvent?.nearestPromotionEvent) {
      nearestPromotionEvent$ = this.renderGrowthbookService.configurations$.pipe(
        first(),
        switchMap((configs) => {
          const growthbookPromotionEvent = this.getGrowthbookPromotionEvent(configs);
          return growthbookPromotionEvent
            ? of(growthbookPromotionEvent)
            : this.getWindowPromotionEvent$().pipe(first());
        })
      );
    }


    this.timeToCountdown$ = combineLatest([
      currentDate$,
      nearestPromotionEvent$,
      interval(1000)
    ]).pipe(
      switchMap(([currentDate, promotionEvent, additionalSeconds]) => {
        if ((promotionEvent?.typeDate === 'startDate' && generalSettings.helperNextEvent?.endDateNextPromotionEvent)
          || (promotionEvent?.typeDate === 'endDate' && !generalSettings.helperNextEvent?.endDateNextPromotionEvent)
        ) {
          return of(undefined);
        }
        return this.finalCountdownService.getTimeToCountdown$(
          currentDate.plus({ seconds: additionalSeconds }),
          generalSettings.typeOfDate === 'local',
          generalSettings.startDate,
          promotionEvent ? promotionEvent.date : generalSettings.endDate,
          units,
          !!promotionEvent
        );
      })
    );
  }

  private getGrowthbookPromotionEvent(configs: GrowthbookConfig[]): PromotionEvent | undefined {
    const date = getStringParam(configs, 'countdown.date');
    const rawTypeDate = getStringParam(configs, 'countdown.date.type');
    const typeDate = rawTypeDate === 'startDate' || rawTypeDate === 'endDate' ? rawTypeDate : undefined;

    if (!date || !typeDate) {
      return undefined;
    }

    return { date, typeDate };
  }

  private getWindowPromotionEvent$(): Observable<PromotionEvent | undefined> {
    const nearestPromotionEventPromise = this.window.getNearestPromotionEvent?.();
    return nearestPromotionEventPromise
      ? from(nearestPromotionEventPromise)
      : of(undefined);
  }

  private getUnits(labelStyle: PieceFinalCountdownSettings['general']['dateStyle']): DurationUnits {
    const units: DurationUnits = [];
    if (labelStyle.showDaysToggle) {
      units.push('days');
    }
    if (labelStyle.showHoursToggle) {
      units.push('hours');
    }
    if (labelStyle.showMinutesToggle) {
      units.push('minutes');
    }
    if (labelStyle.showSecondsToggle) {
      units.push('seconds');
    }
    return units.concat(['milliseconds']);
  }

}
